/**
 * メッセージ・タスク表示エリアコンポーネント
 *
 * ナビゲーション右上部の直下に配置され、開閉可能なメッセージ・タスク表示エリアを提供します。
 * 既存のPDFプレビュー操作エリア・入力操作エリアのデザインを踏襲しています。
 */

import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import NotificationsIcon from '@mui/icons-material/Notifications';
import TaskIcon from '@mui/icons-material/Task';

import {
  Avatar,
  Badge,
  Box,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { FOCUS_MODE_BACKGROUNDS, getFocusModeValue } from '@src/constants/design-tokens';
import { DIMENSIONS } from '@src/constants/dimensions';
import { useMessageTaskData } from '@src/hooks/useMessageTaskData';
import { selectIsAuthenticated } from '@src/store/slices/auth';
import { selectFocusMode } from '@src/store/slices/focus_mode';
import { memo, type ReactElement, useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import { GenericMessageItem } from './GenericMessageItem';

/**
 * メッセージ・タスク表示エリアコンポーネントのProps型定義
 */
type MessageTaskPanelProps = {
  isScrolling?: boolean;
};

/**
 * メッセージ・タスク表示エリアコンポーネント
 *
 * @param {MessageTaskPanelProps} props - コンポーネントのプロパティ
 * @returns {ReactElement} メッセージ・タスク表示エリアコンポーネント
 */
export const MessageTaskPanel = memo(
  ({ isScrolling = false }: MessageTaskPanelProps): ReactElement | null => {
    // 認証状態を取得
    const isAuthenticated = useSelector(selectIsAuthenticated);
    // フォーカスモード状態を取得
    const isFocusMode = useSelector(selectFocusMode);

    // 状態管理
    const [isOpen, setIsOpen] = useState(false);
    const [currentTab, setCurrentTab] = useState(0);

    const {
      messages,
      tasks,
      unreadCount,
      pendingTaskCount,
      handleMessageClick,
      getMessageColor,
      getTaskColor,
      getTaskStatusText,
    } = useMessageTaskData();

    const theme = useTheme();
    const accentColor = theme.palette.primary.main;
    const neutralAccent = theme.palette.grey[500];
    const surfaceColor = getFocusModeValue(FOCUS_MODE_BACKGROUNDS, 'primary', isFocusMode);
    const headerBackground = `linear-gradient(140deg, ${alpha(
      accentColor,
      isOpen ? 0.18 : 0.12
    )} 0%, ${alpha(neutralAccent, isOpen ? 0.18 : 0.12)} 35%, ${surfaceColor} 100%)`;
    const outlineColor = alpha(
      isFocusMode ? theme.palette.common.white : theme.palette.grey[500],
      0.25
    );
    const contentBackground = alpha(surfaceColor, 0.98);
    const headerTextColor = isFocusMode
      ? alpha(theme.palette.common.white, 0.9)
      : theme.palette.text.primary;
    const headerSubTextColor = isFocusMode
      ? alpha(theme.palette.common.white, 0.75)
      : alpha(theme.palette.text.secondary, 0.9);

    /**
     * 開閉トグル
     */
    const handleToggle = useCallback(() => {
      setIsOpen((prev) => !prev);
    }, []);

    /**
     * タブ切り替え
     */
    const handleTabChange = useCallback((_event: React.SyntheticEvent, newValue: number) => {
      setCurrentTab(newValue);
    }, []);

    // 認証されていない場合は非表示
    if (!isAuthenticated) {
      return null;
    }

    return (
      <Box
        sx={{
          position: 'absolute',
          top: { xs: 54, sm: 60 },
          right: { xs: 10, sm: 24 },
          zIndex: 9998,
          width: { xs: 'calc(100% - 32px)', sm: 264, md: 280 },
          maxWidth: 320,
          transition: 'transform 0.2s ease, opacity 0.2s ease',
          transform: isOpen ? 'translateY(0)' : 'translateY(2px)',
          opacity: isScrolling ? 0.3 : 0.5,
          '&:hover': {
            opacity: 1,
            transform: 'translateY(0)',
          },
        }}
      >
        {/* ヘッダー部分 */}
        <Box
          role="toolbar"
          aria-label="メッセージ・タスクコントロール"
          sx={{
            backgroundImage: headerBackground,
            borderRadius: isOpen ? '12px 12px 0 0' : '12px',
            border: `1px solid ${outlineColor}`,
            padding: { xs: '8px 12px', sm: '9px 14px' },
            display: 'flex',
            flexWrap: { xs: 'wrap', sm: 'nowrap' },
            alignItems: 'center',
            columnGap: 1,
            rowGap: 0.75,
            cursor: 'pointer',
            boxShadow: isOpen
              ? `0 14px 24px ${alpha(theme.palette.common.black, 0.18)}`
              : `0 8px 16px ${alpha(theme.palette.common.black, 0.12)}`,
            backdropFilter: 'blur(6px)',
            transition: 'border-radius 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease',
            '&:hover': {
              borderColor: alpha(accentColor, 0.4),
            },
          }}
          onClick={handleToggle}
        >
          <Stack direction="row" spacing={1} alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
            <Avatar
              variant="rounded"
              sx={{
                width: DIMENSIONS.ICON_SIZE_EXTRA_LARGE,
                height: DIMENSIONS.ICON_SIZE_EXTRA_LARGE,
                borderRadius: 2,
                border: `1px solid ${alpha(accentColor, 0.2)}`,
                backgroundColor: alpha(accentColor, 0.08),
                color: headerTextColor,
                boxShadow: 'none',
              }}
            >
              <NotificationsIcon fontSize="small" />
            </Avatar>
            <Box sx={{ minWidth: 0 }}>
              <Typography
                variant="caption"
                sx={{
                  letterSpacing: 0.8,
                  fontWeight: 600,
                  color: headerSubTextColor,
                }}
              >
                通知エリア
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  color: headerTextColor,
                  whiteSpace: { xs: 'normal', sm: 'nowrap' },
                  lineHeight: 1.35,
                }}
              >
                未読 {unreadCount} 件 ・ タスク {pendingTaskCount} 件
              </Typography>
            </Box>
          </Stack>
          <IconButton
            size="small"
            aria-label={isOpen ? '閉じる' : '開く'}
            onClick={(event) => {
              event.stopPropagation();
              handleToggle();
            }}
            sx={{
              color: headerTextColor,
              border: `1px solid ${alpha(headerTextColor, 0.2)}`,
              backgroundColor: alpha(surfaceColor, isFocusMode ? 0.25 : 0.85),
              boxShadow: 'none',
              width: DIMENSIONS.ICON_SIZE_LARGE,
              height: DIMENSIONS.ICON_SIZE_LARGE,
              borderRadius: '50%',
              '&:hover': {
                backgroundColor: alpha(surfaceColor, 1),
              },
            }}
          >
            {isOpen ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
          </IconButton>
        </Box>

        {/* コンテンツ部分 */}
        {isOpen && (
          <Box
            sx={{
              backgroundColor: contentBackground,
              borderRadius: '0 0 12px 12px',
              boxShadow: `0 16px 32px ${alpha(theme.palette.common.black, 0.18)}`,
              border: `1px solid ${outlineColor}`,
              borderTop: 'none',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {/* タブ */}
            <Tabs
              value={currentTab}
              onChange={handleTabChange}
              variant="fullWidth"
              sx={{
                px: { xs: 1.25, sm: 1.5 },
                pt: 1.25,
                '& .MuiTabs-flexContainer': {
                  gap: 0.5,
                },
                '& .MuiTab-root': {
                  minHeight: '32px',
                  borderRadius: '999px',
                  textTransform: 'none',
                  fontSize: '0.72rem',
                  fontWeight: 600,
                  color: alpha(headerTextColor, 0.65),
                  backgroundColor: alpha(theme.palette.common.white, isFocusMode ? 0.06 : 0.85),
                  boxShadow: 'none',
                },
                '& .MuiTab-root.Mui-selected': {
                  color: headerTextColor,
                  backgroundColor: alpha(accentColor, 0.12),
                  boxShadow: 'none',
                },
                '& .MuiTabs-indicator': {
                  display: 'none',
                },
              }}
            >
              <Tab
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <NotificationsIcon fontSize="small" />
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      メッセージ
                      {unreadCount > 0 && (
                        <Badge
                          badgeContent={unreadCount}
                          color="error"
                          sx={{
                            ml: 0.75,
                            '& .MuiBadge-badge': {
                              minWidth: 16,
                              height: 16,
                              fontSize: '0.6rem',
                              fontWeight: 600,
                              boxShadow: 'none',
                            },
                          }}
                        />
                      )}
                    </Typography>
                  </Box>
                }
                sx={{ minHeight: '32px', fontSize: '0.72rem', px: 1.25 }}
              />
              <Tab
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <TaskIcon fontSize="small" />
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      タスク
                      {pendingTaskCount > 0 && (
                        <Badge
                          badgeContent={pendingTaskCount}
                          color="primary"
                          sx={{
                            ml: 0.75,
                            '& .MuiBadge-badge': {
                              minWidth: 16,
                              height: 16,
                              fontSize: '0.6rem',
                              fontWeight: 600,
                              boxShadow: 'none',
                            },
                          }}
                        />
                      )}
                    </Typography>
                  </Box>
                }
                sx={{ minHeight: '32px', fontSize: '0.72rem', px: 1.25 }}
              />
            </Tabs>

            {/* メッセージリスト */}
            {currentTab === 0 && (
              <List
                sx={{
                  maxHeight: '220px',
                  overflowY: 'auto',
                  pt: 0.75,
                  pb: 1.25,
                  px: { xs: 0.75, sm: 1.25 },
                  '&::-webkit-scrollbar': {
                    width: 6,
                  },
                  '&::-webkit-scrollbar-thumb': {
                    borderRadius: 999,
                    backgroundColor: alpha(accentColor, 0.18),
                  },
                }}
              >
                {messages.length === 0 ? (
                  <ListItem
                    sx={{
                      justifyContent: 'center',
                      borderRadius: 1.5,
                      border: `1px dashed ${alpha(headerTextColor, 0.2)}`,
                      py: 1.5,
                      px: 1.25,
                      backgroundColor: alpha(theme.palette.common.white, isFocusMode ? 0.05 : 0.85),
                    }}
                  >
                    <ListItemText
                      primary="メッセージはありません"
                      primaryTypographyProps={{
                        sx: {
                          fontSize: '0.8rem',
                          textAlign: 'center',
                          color: headerSubTextColor,
                          fontWeight: 500,
                        },
                      }}
                    />
                  </ListItem>
                ) : (
                  messages.map((message) => (
                    <GenericMessageItem
                      key={message.id}
                      id={message.id}
                      title={message.title}
                      content={message.content}
                      borderColor={getMessageColor(message.type)}
                      isRead={message.isRead}
                      onItemClick={handleMessageClick}
                    />
                  ))
                )}
              </List>
            )}

            {/* タスクリスト */}
            {currentTab === 1 && (
              <List
                sx={{
                  maxHeight: '220px',
                  overflowY: 'auto',
                  pt: 0.75,
                  pb: 1.25,
                  px: { xs: 0.75, sm: 1.25 },
                  '&::-webkit-scrollbar': {
                    width: 6,
                  },
                  '&::-webkit-scrollbar-thumb': {
                    borderRadius: 999,
                    backgroundColor: alpha(accentColor, 0.18),
                  },
                }}
              >
                {tasks.length === 0 ? (
                  <ListItem
                    sx={{
                      justifyContent: 'center',
                      borderRadius: 1.5,
                      border: `1px dashed ${alpha(headerTextColor, 0.2)}`,
                      py: 1.5,
                      px: 1.25,
                      backgroundColor: alpha(theme.palette.common.white, isFocusMode ? 0.05 : 0.85),
                    }}
                  >
                    <ListItemText
                      primary="タスクはありません"
                      primaryTypographyProps={{
                        sx: {
                          fontSize: '0.8rem',
                          textAlign: 'center',
                          color: headerSubTextColor,
                          fontWeight: 500,
                        },
                      }}
                    />
                  </ListItem>
                ) : (
                  tasks.map((task) => (
                    <GenericMessageItem
                      key={task.id}
                      id={task.id}
                      title={task.title}
                      content={task.description}
                      borderColor={getTaskColor(task.status)}
                      isRead={task.status === 'completed'}
                      statusText={getTaskStatusText(task.status)}
                      statusColor={getTaskColor(task.status)}
                    />
                  ))
                )}
              </List>
            )}
          </Box>
        )}
      </Box>
    );
  }
);

MessageTaskPanel.displayName = 'MessageTaskPanel';
